import React, {useContext, useEffect, useState} from "react";
import {UserContext} from "../../context/UserContext";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import {LanguageContext} from "../../context/LanguageContext";
import {adminFinTechRight} from "../../utils/commonValues";
import {Box, Menu, MenuItem} from "@mui/material";
import UserMenuMobileDialog from "../dialog/UserMenuMobileDialog";
import {changePassword, configureTotp, contactHasRight, getUserFullName} from "../../utils/commonFunctions";
import SimpleConfirmDialog from "../dialog/SimpleConfirmDialog";
import PropTypes from "prop-types";
import ProfileButtonTooltip from "./ProfileButtonTooltip";
import {CustomerContext} from "../../context/CustomerContext";
import Rendered from "../Rendered";
import CompanySelectorDialog from "../dialog/CompanySelectorDialog";
import {useHistory} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import {InfoOutlined} from "@mui/icons-material";
import {useMediaQuery} from "@mui/system";
import {useTheme} from "@mui/styles";

const ProfileButton = ({renderMiniMenu}) => {
  const theme = useTheme();
  const history = useHistory();
  const languageContext = useContext(LanguageContext);
  const userContext = useContext(UserContext);
  const customerContext = useContext(CustomerContext);
  const [userMenuMobileDialogOpen, setUserMenuMobileDialogOpen] = useState(false);
  const [mfaConfirmDialogOpen, setMfaConfirmDialogOpen] = useState(false);
  const [multipleCompanies, setMultipleCompanies] = useState(true);
  const [companySelectorDialogOpen, setCompanySelectorDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const renderMiniOrMobileMenu = renderMiniMenu || mobileView;

  useEffect(() => {
    if (userContext?.userInfo?.email && userContext?.userInfo?.email !== "" && customerContext.selectedContact.loaded) {
      setMultipleCompanies(userContext.userInfo.innerUser || userContext.userInfo.companyList?.length > 1);
      setCompanySelectorDialogOpen(!customerContext?.selectedCustomer?.id || !customerContext?.selectedContact?.id);
    }
  }, [userContext.userInfo, customerContext.selectedContact]);

  const buttonList = [
    {
      label: languageContext.dictionary.companySelector,
      onClick: () => setCompanySelectorDialogOpen(true),
      hidden: !multipleCompanies || renderMiniOrMobileMenu
    },
    {
      label: languageContext.dictionary.companyInformation,
      icon: "business",
      onClick: () => history.push("/companyDetails")
    },
    {
      label: languageContext.dictionary.myProfile,
      icon: "account_box",
      onClick: () => history.push("/profile")
    },
    {
      label: languageContext.dictionary.users,
      icon: "lock_open",
      onClick: () => history.push("/usersAndPermissions"),
      hidden: !contactHasRight(customerContext.selectedContact.roles, adminFinTechRight)
    },
    {
      label: languageContext.dictionary.changePassword,
      icon: "lock_reset",
      onClick: () => changePassword()
    },
    {
      label: "MFA",
      icon: "system_security_update_good",
      onClick: () => setMfaConfirmDialogOpen(true),
      tooltip: languageContext.dictionary.configureMfaExperimentalFeature,
      info: <InfoOutlined fontSize="small" color="disabled" sx={{marginLeft: 0.5}}/>
    }
  ];

  return (
    <Box sx={{...(renderMiniOrMobileMenu && {alignItems: "center", display: "flex", justifyContent: "center", width: "100%"})}}>
      <Box>
        <ProfileButtonTooltip>
          {renderMiniOrMobileMenu ? (
            <IconButton
              sx={{color: "secondary.contrastText", marginRight: 2}}
              style={{fontSize: 30}}
              onClick={event => mobileView ? setUserMenuMobileDialogOpen(true) : setAnchorEl(event.currentTarget)}
            >
              <AccountCircleOutlinedIcon/>
            </IconButton>
          ) : (
            <Button
              data-testid="loggedInUserMenu"
              sx={{textTransform: "none"}}
              startIcon={<AccountCircleOutlinedIcon sx={{color: "secondary.main"}} style={{fontSize: 30}}/>}
              onClick={event => setAnchorEl(event.currentTarget)}
            >
              <Box sx={{fontWeight: "normal"}}>
                {getUserFullName(userContext.userInfo, customerContext.selectedContact, languageContext.language.id)}
                <Box
                  sx={{
                    color: "secondary.main",
                    maxWidth: 120,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {customerContext?.selectedCustomer?.name || ""}
                </Box>
              </Box>
            </Button>
          )}
        </ProfileButtonTooltip>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          sx={{
            '& .MuiPaper-root': {
              width: "100%",
              maxWidth: 180
            }
          }}
        >
          {buttonList.map((button, index) =>
            <Rendered key={index} rendered={!button.hidden}>
              <Tooltip title={button.tooltip || null} arrow>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    button.onClick?.();
                  }}
                >
                  {button.label} {button.info}
                </MenuItem>
              </Tooltip>
            </Rendered>
          )}
        </Menu>
      </Box>
      <UserMenuMobileDialog
        open={userMenuMobileDialogOpen}
        handleClose={() => setUserMenuMobileDialogOpen(false)}
        buttonList={buttonList}
      />
      <SimpleConfirmDialog
        open={mfaConfirmDialogOpen}
        handleClose={() => setMfaConfirmDialogOpen(false)}
        handleAction={configureTotp}
        actionLabel={languageContext.dictionary.next}
        dialogTitle={languageContext.dictionary.configureMfa}
        dialogContentText={languageContext.dictionary.configureMfaDialog}
      />
      <Rendered rendered={!!multipleCompanies}>
        <CompanySelectorDialog
          open={companySelectorDialogOpen}
          handleClose={() => setCompanySelectorDialogOpen(false)}
        />
      </Rendered>
    </Box>
  );
};

ProfileButton.propTypes = {
  renderMiniMenu: PropTypes.bool
};

export default ProfileButton;
